import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { isValidToken, setSession } from './utils';
import { ActionMapType, AuthStateType, AuthUserType, JWTContextType, LoginResponseType } from './types';
import { getLogin } from '../api'
// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      
      // 
      if (accessToken&&isValidToken(accessToken)) {
          setSession(accessToken);
          // const res:any = await getConfigData()
          // if(res.code===0) {
          //   var obj:any = {
              
          //   }
          //   res.data.map((v:any,i:number)=>{
          //       v.config_data = JSON.parse(v.config_data)
          //       obj[v.key] = v.config_data
          //   })
          //   localStorage.setItem('config',JSON.stringify(obj))
          // }
        // const response = await axios.get('/api/account/my-account');

        // const { user } = response.data;
        // const { user } = {
        //   user:{
        //     about: "Praesent turpis. Phasellus viverra nulla ut metus varius laoreet. Phasellus tempus.",
        //     address: "90210 Broadway Blvd",
        //     city: "San Francisco",
        //     country: "United States",
        //     display_name: "杨晨",
        //     email: "946754060@qq.com",
        //     id: "8864c717-587d-472a-929a-8e5f298024da-0",
        //     is_public: true,
        //     password: "demo1234",
        //     phone_number: "+40 777666555",
        //     photo_url: "https://api-dev-minimal-v4.vercel.app/assets/images/avatars/avatar_default.jpg",
        //     role: "admin",
        //     state: "California",
        //     zip_code: "94116",
        //   }
        // }
        // 框架原个人信息实时获取存在reduce，目前不存在实时获取接口所以登录时存储后再取出存入保证reduce实时存在
        const userStr = localStorage.getItem('user');
        let user: AuthUserType = null;
        
        try {
          if (userStr) {
            user = JSON.parse(userStr);
          }
        } catch (error) {
          console.error('解析用户信息失败:', error);
          // 如果解析失败，清除无效数据
          localStorage.removeItem('user');
        }
        
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (account: string, password: string) => {
    const response = await getLogin({
      email: account,
      password,
    }) as LoginResponseType;
    
    // 检查响应状态码
    if (response.code !== 200) {
      const error = new Error(response.message || "登录失败");
      (error as any).data = response;
      throw error;
    }
    
    // 检查是否有access_token
    if (!response.data.access_token) {
      throw new Error("请检查输入的账号密码验证码");
    }
    
    const { access_token: accessToken, user, refresh_token: refreshToken } = response.data;
    
    // 从JWT token中解析用户角色信息
    let enhancedUser = user;
    if (user && accessToken) {
      try {
        const decodedToken = JSON.parse(atob(accessToken.split('.')[1]));
        enhancedUser = {
          ...user,
          role: decodedToken.role
        };
      } catch (error) {
        console.error('解析JWT token失败:', error);
      }
    }
    
    // 设置会话信息
    setSession(accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', JSON.stringify(enhancedUser));
    
    // 更新认证状态
    setTimeout(() => {
      dispatch({
        type: Types.LOGIN,
        payload: {
          user: enhancedUser,
        },
      });
      initialize();
    }, 500);
  }, [initialize]);

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      const response = await axios.post('/api/account/register', {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });
      const { access_token: accessToken, user } = response.data;

      localStorage.setItem('accessToken', accessToken);

      dispatch({
        type: Types.REGISTER,
        payload: {
          user,
        },
      });
    },
    []
  );

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      loginWithGoogle: () => {},
      loginWithGithub: () => {},
      loginWithTwitter: () => {},
      register,
      logout,
    }),
    [state.isAuthenticated, state.isInitialized, state.user, login, logout, register]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
